import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTodayProgress } from '../api';
import { useAuth } from '../contexts/AuthContext';
import GlassCard from './ui/GlassCard';

const DailyGoalWidget = ({ onOpenSettings }) => {
    const { session } = useAuth();
    const navigate = useNavigate();
    const [progress, setProgress] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (session) {
            fetchProgress();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session]);

    const fetchProgress = async () => {
        try {
            setLoading(true);
            setError(null);
            const token = session?.access_token;
            if (!token) {
                setError('Not authenticated');
                setLoading(false);
                return;
            }

            const data = await getTodayProgress(token);
            setProgress(data);
        } catch (err) {
            console.error('Error fetching today\'s progress:', err);
            setError('Failed to load progress');
        } finally {
            setLoading(false);
        }
    };

    const getProgressPercentage = () => {
        if (!progress) return 0;
        return Math.min((progress.applications_today / progress.daily_goal) * 100, 100);
    };

    const percentage = getProgressPercentage();

    // Calculate stroke-dashoffset for SVG circle (circumference = 2 * PI * radius)
    const circumference = 2 * Math.PI * 52; // radius = 52
    const offset = circumference - (percentage / 100) * circumference;

    const styles = {
        container: {
            padding: 'var(--space-xl)',
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--space-lg)',
        },
        title: {
            fontFamily: 'var(--font-display)',
            fontSize: '1.1rem',
            fontWeight: 600,
            color: 'var(--text-primary)',
        },
        status: {
            fontSize: '0.75rem',
            padding: 'var(--space-xs) var(--space-sm)',
            borderRadius: 'var(--radius-sm)',
        },
        statusInProgress: {
            background: 'rgba(34, 211, 238, 0.15)',
            color: 'var(--cyan-400)',
        },
        statusComplete: {
            background: 'rgba(16, 185, 129, 0.15)',
            color: 'var(--green-500)',
        },
        progressContainer: {
            position: 'relative',
            width: '120px',
            height: '120px',
            margin: '0 auto var(--space-lg)',
        },
        progressRing: {
            width: '100%',
            height: '100%',
            transform: 'rotate(-90deg)',
        },
        ringBg: {
            fill: 'none',
            stroke: 'var(--bg-surface)',
            strokeWidth: 8,
        },
        ringFill: {
            fill: 'none',
            stroke: 'url(#progress-gradient)',
            strokeWidth: 8,
            strokeLinecap: 'round',
            strokeDasharray: circumference,
            strokeDashoffset: offset,
            transition: 'stroke-dashoffset 1s var(--ease-out)',
        },
        progressCenter: {
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
        },
        progressPercent: {
            display: 'block',
            fontFamily: 'var(--font-display)',
            fontSize: '1.75rem',
            fontWeight: 700,
            background: 'var(--gradient-primary)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
        },
        progressLabel: {
            fontSize: '0.75rem',
            color: 'var(--text-tertiary)',
        },
        tasks: {
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-sm)',
        },
        task: {
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-sm)',
            fontSize: '0.85rem',
            color: 'var(--text-secondary)',
        },
        taskCompleted: {
            color: 'var(--text-tertiary)',
            textDecoration: 'line-through',
        },
        taskIcon: {
            width: '18px',
            height: '18px',
            flexShrink: 0,
        },
        taskIconComplete: {
            color: 'var(--cyan-400)',
        },
        settingsBtn: {
            color: 'var(--indigo-400)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: '0.8rem',
            fontWeight: 600,
            transition: 'color var(--duration-fast)',
        },
        browseBtn: {
            display: 'block',
            width: '100%',
            textAlign: 'center',
            background: 'var(--gradient-primary)',
            color: 'var(--text-primary)',
            fontWeight: 600,
            padding: 'var(--space-sm) var(--space-md)',
            borderRadius: 'var(--radius-md)',
            border: 'none',
            cursor: 'pointer',
            fontSize: '0.9rem',
            marginTop: 'var(--space-md)',
            transition: 'transform var(--duration-fast), box-shadow var(--duration-fast)',
        },
        skeleton: {
            padding: 'var(--space-xl)',
        },
        skeletonLine: {
            height: '16px',
            background: 'var(--bg-surface)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--space-md)',
        },
    };

    if (loading) {
        return (
            <GlassCard style={styles.skeleton}>
                <div style={{ ...styles.skeletonLine, width: '50%' }} />
                <div style={{ ...styles.skeletonLine, width: '75%', height: '100px' }} />
                <div style={{ ...styles.skeletonLine, width: '60%' }} />
            </GlassCard>
        );
    }

    if (error) {
        return (
            <GlassCard style={{ padding: 'var(--space-xl)' }}>
                <p style={{ color: 'var(--red-500)' }}>Failed to load daily goal</p>
            </GlassCard>
        );
    }

    if (!progress) return null;

    const { applications_today, daily_goal, goal_met } = progress;
    const remaining = Math.max(daily_goal - applications_today, 0);

    return (
        <GlassCard style={styles.container}>
            {/* Header */}
            <div style={styles.header}>
                <h3 style={styles.title}>Daily Goal</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
                    <span style={{
                        ...styles.status,
                        ...(goal_met ? styles.statusComplete : styles.statusInProgress)
                    }}>
                        {goal_met ? 'Complete' : 'In Progress'}
                    </span>
                    {onOpenSettings && (
                        <button
                            style={styles.settingsBtn}
                            onClick={onOpenSettings}
                            onMouseEnter={(e) => e.target.style.color = 'var(--text-primary)'}
                            onMouseLeave={(e) => e.target.style.color = 'var(--indigo-400)'}
                        >
                            Settings
                        </button>
                    )}
                </div>
            </div>

            {/* Progress Ring */}
            <div style={styles.progressContainer}>
                <svg style={styles.progressRing} viewBox="0 0 120 120">
                    <defs>
                        <linearGradient id="progress-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="var(--indigo-500)" />
                            <stop offset="100%" stopColor="var(--cyan-400)" />
                        </linearGradient>
                    </defs>
                    <circle style={styles.ringBg} cx="60" cy="60" r="52" />
                    <circle style={styles.ringFill} cx="60" cy="60" r="52" />
                </svg>
                <div style={styles.progressCenter}>
                    <span style={styles.progressPercent}>{Math.round(percentage)}%</span>
                    <span style={styles.progressLabel}>Complete</span>
                </div>
            </div>

            {/* Task List / Status */}
            <div style={styles.tasks}>
                <div style={{ ...styles.task, ...(applications_today > 0 ? styles.taskCompleted : {}) }}>
                    {applications_today > 0 ? (
                        <svg style={{ ...styles.taskIcon, ...styles.taskIconComplete }} viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"/>
                        </svg>
                    ) : (
                        <svg style={styles.taskIcon} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="10" cy="10" r="7"/>
                        </svg>
                    )}
                    <span>Apply to {daily_goal} jobs</span>
                </div>

                {goal_met ? (
                    <div style={{ ...styles.task, color: 'var(--green-500)' }}>
                        <span style={{ fontSize: '1.2rem' }}>🎉</span>
                        <span style={{ fontWeight: 500 }}>Goal complete! Great work.</span>
                    </div>
                ) : (
                    <div style={styles.task}>
                        <span style={{ fontSize: '1rem' }}>🎯</span>
                        <span><strong style={{ color: 'var(--text-primary)' }}>{remaining}</strong> more to hit your goal</span>
                    </div>
                )}
            </div>

            {/* Browse Jobs Button */}
            {!goal_met && (
                <button
                    style={styles.browseBtn}
                    onClick={() => navigate('/discover-jobs')}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = 'var(--glow-indigo)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                    }}
                >
                    Browse Jobs
                </button>
            )}
        </GlassCard>
    );
};

export default DailyGoalWidget;
